import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";

import {
  getGetOrderQueryKey,
  getGetOrdersQueryKey,
  useGetOrder,
  useGetOrderPaymentLink,
  useUpdateOrderStatus,
} from "@/api";
import type { GetOrdersStatus } from "@/api/schemas/getOrdersStatus";

export function useOrderDetail(orderId: number) {
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useGetOrder(orderId, {
    query: { enabled: orderId > 0 },
  });

  const order = data?.status === 200 ? data.data.data : undefined;

  const { data: paymentLinkData } = useGetOrderPaymentLink(orderId, {
    query: { enabled: orderId > 0 && Boolean(order) },
  });

  const paymentLink =
    paymentLinkData?.status === 200 ? paymentLinkData.data.data.checkout_url : undefined;

  const { mutate: updateStatus, isPending: isUpdatingStatus } = useUpdateOrderStatus({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetOrderQueryKey(orderId) });
        void queryClient.invalidateQueries({ queryKey: getGetOrdersQueryKey() });
      },
    },
  });

  const handleUpdateStatus = useCallback(
    (status: GetOrdersStatus) => {
      updateStatus({ id: orderId, data: { status } });
    },
    [orderId, updateStatus],
  );

  return {
    order,
    paymentLink,
    isLoading,
    isError,
    isUpdatingStatus,
    updateStatus: handleUpdateStatus,
  };
}
